import { Injectable } from '@angular/core';
import { AwsRum } from 'aws-rum-web';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  CognitoIdentityClient,
  GetIdCommand,
  GetCredentialsForIdentityCommand
} from '@aws-sdk/client-cognito-identity';
import { environment } from 'src/environments/environment';
import { TokenService } from '@core/services/token.service';

@Injectable({
  providedIn: 'root'
})
export class RumService {
  private rum: AwsRum | null = null;
  private isInitialised = false;

  constructor(private tokenService: TokenService) {}

  async initialise(): Promise<void> {
    if (this.isInitialised) return;
    this.isInitialised = true;

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        console.warn('[RUM] No id_token available, skipping initialisation');
        this.isInitialised = false;
        return;
      }

      const loginKey = `cognito-idp.us-east-1.amazonaws.com/${environment.userPoolId}`;
      const client = new CognitoIdentityClient({ region: 'us-east-1' });

      const { IdentityId } = await client.send(new GetIdCommand({
        IdentityPoolId: environment.identityPoolId,
        Logins: { [loginKey]: idToken },
      }));

      const { Credentials } = await client.send(new GetCredentialsForIdentityCommand({
        IdentityId,
        Logins: { [loginKey]: idToken },
      }));

      if (!Credentials?.AccessKeyId || !Credentials?.SecretKey || !Credentials?.SessionToken) {
        console.warn('[RUM] Incomplete credentials, skipping initialisation');
        this.isInitialised = false;
        return;
      }

      this.rum = new AwsRum(
        environment.rumAppMonitorId,
        '1.0.0',
        'us-east-1',
        {
          sessionSampleRate: 1,
          telemetries: ['performance', 'http', 'errors'],
          allowCookies: true,
          endpoint: 'https://dataplane.rum.us-east-1.amazonaws.com',
        }
      );

      this.rum.setAwsCredentials({
        accessKeyId: Credentials.AccessKeyId,
        secretAccessKey: Credentials.SecretKey,
        sessionToken: Credentials.SessionToken,
      });

      const decoded = this.tokenService.getDecodedToken();
      this.rum.addSessionAttributes({
        email: decoded?.email ?? 'unknown',
        groups: (decoded?.['cognito:groups'] ?? []).join(','),
        scope: decoded?.scope ?? 'unknown',
      });

    } catch (error) {
      console.warn('[RUM] Failed to initialise:', error);
      this.isInitialised = false;
    }
  }

  recordError(error: unknown): void {
    this.rum?.recordError(error);
  }

  recordPageView(pageId: string): void {
    this.rum?.recordPageView(pageId);
  }
}